import React, { FC, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import {
    Spin,
    Drawer,
    Form,
    Input,
    Row,
    Col,
    Button,
    Divider,
    Alert,
    Icon,
    Tree,
    TreeSelect,
} from 'antd';
import { FormComponentProps } from 'antd/lib/form';
import { DrawerProps } from 'antd/lib/drawer';
import { ButtonProps } from 'antd/lib/button';
import Upload, { RcFile } from 'antd/lib/upload';
import { UploadFile } from 'antd/lib/upload/interface';

import {
    create, details, update, clean, DocumentsState, getDocumentCreateState,
    getDocumentDetailsState, getDocumentUpdateState, structure } from '../../store/actions/documents';
import { MainReducerState } from '../../store/reducers';
import { Document, Section, MimeTypes } from '../../store/api/apiTypes';
import moment from 'moment';
import { getSectionsListState, SectionsState } from '../../store/actions/sections';
import { getErrorMessageFromState } from '../../utils';
const { TreeNode } = Tree;

interface DocumentDrawerProps extends FormComponentProps {
    creates: DocumentsState['create'];
    createDocument: typeof create.trigger;
    getDetails: typeof details.trigger;
    cleanDetails?: any;
    section?: Section;
    year?: string;
    id?: Document['id'];
    isVisible: boolean;
    onClose: () => void;
    onUpdateSuccess?: () => void;
    documentState: DocumentsState['details'];
    sectionsState: SectionsState['list'];
    updateDocument: typeof update.trigger;
    updates: DocumentsState['update'];
    getStructure: typeof structure.trigger;
}

const DocumentDrawer: FC<DocumentDrawerProps> = ({
    creates, createDocument, documentState, form, getDetails, cleanDetails, id, isVisible, onClose,
    onUpdateSuccess, section, updateDocument, updates, year, sectionsState, getStructure,
}) => {

    const [isEditing, setIsEditing] = useState(false);
    const [file, setFile] = useState<RcFile>();
    const [fileList, setFileList] = useState < Array<UploadFile < any > >>([]);

    useEffect(() => {
        if (creates.success || updates.success) {
            form.resetFields();
            if (typeof onUpdateSuccess === 'function') {
                onUpdateSuccess();
            }
            onClose();
            getStructure();
            setFile (undefined);
            setFileList([]);
        }
    }, [creates.success, updates.success]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (isVisible) {
            if (id !== undefined) {
                getDetails({ id });
            } else {
                cleanDetails();
             //   setFile(undefined);
            }
        }
        setIsEditing(!!id);
    }, [isVisible]); // eslint-disable-line react-hooks/exhaustive-deps

    const onDrawerClose: DrawerProps['onClose'] & ButtonProps['onClick'] = () => {
        onClose();
        form.resetFields();
    };

    const onSectionChange = (value: any) => {
        // form.setFieldsValue({ section: newSection });
    };

    const onSubmit = (e?: React.FormEvent) => {
        if (e) {
            e.preventDefault();
        }
        form.validateFieldsAndScroll(async (err, val) => {
            if (err) {
                return;
            }
            const payload = {
                ...val,
            };
            if (file) {
                payload.file = file;
                payload.type = file.type;
            }

            if (id !== undefined) {
                updateDocument({ id, data: payload });
            } else {
                createDocument({ data: payload });
            }
        });
    };

    const beforeFileUpload = (newFile: RcFile) => {
        setFile(newFile);
        return false;
    };

    const onFileRemove = () => {
        setFile(undefined);
    };

    const onFileChange = (fl: any) => {
        setFileList(fl.fileList);
    };

    const { getFieldDecorator } = form;

    const createErrorMessage = getErrorMessageFromState(creates);
    const updateErrorMessage = getErrorMessageFromState(updates);

    return (
        <>
            <Drawer
                title={id !== undefined ? `Edition du document` : `Creation de document`}
                width={960}
                onClose={onDrawerClose}
                visible={isVisible}
            >
                <Spin spinning={documentState.loading} tip="Chargement...">
                    {(!isEditing || (isEditing && !documentState.loading && documentState.data)) && (
                        <Form onSubmit={onSubmit} layout="vertical">
                            <Row gutter={16}>
                                <Col span={12}>
                                    { !isEditing && (
                                    <Form.Item label="Document">
                                        {getFieldDecorator('file', {
                                            rules: [{
                                                required: false,
                                                message: 'champ requis',
                                            }],
                                            initialValue: isEditing && documentState.data ?
                                                documentState.data.file : undefined,
                                        })((
                                        <Upload
                                            beforeUpload={beforeFileUpload}
                                            onRemove={onFileRemove}
                                            fileList={fileList}
                                            onChange={onFileChange}
                                        >
                                            {file === undefined && (
                                                <Button>
                                                    <Icon type="upload" /> Choisir un fichier
                                                </Button>
                                            )}
                                        </Upload>
                                        ))}
                                    </Form.Item>
                                    )}
                                </Col>
                                <Col span={8}>
                                    <Form.Item label="Rubrique">
                                        {getFieldDecorator('section', {
                                            rules: [{
                                                required: true,
                                                message: 'champ requis',
                                            }],
                                            initialValue: isEditing && documentState.data ?
                                            documentState.data.section.id :
                                            section ? section.id : '',
                                        })((
                                            <TreeSelect
                                                showSearch
                                                style={{ width: '100%' }}
                                                dropdownStyle={{ maxHeight: 400, overflow: 'auto' }}
                                                placeholder="Choisissez une rubrique"
                                                allowClear
                                                onChange={onSectionChange}
                                                treeDefaultExpandAll
                                            >
                                                {sectionsState.data &&
                                                    sectionsState.data.items.map((s: Section) => (
                                                    <TreeNode
                                                        title={s.short}
                                                        key={s.id}
                                                        value={s.id}
                                                        icon={<Icon type={s.picto} />}
                                                    >
                                                        {s.children && s.children.map((sub: Section) => (
                                                            <TreeNode
                                                                title={sub.short}
                                                                key={sub.id}
                                                                value={sub.id}
                                                                icon={<Icon type={sub.picto} />}
                                                            />
                                                        ))}
                                                    </TreeNode>
                                                ))}
                                            </TreeSelect>
                                        ))}
                                    </Form.Item>
                                </Col>
                                <Col span={4}>
                                    <Form.Item label="Année">
                                        {getFieldDecorator('year', {
                                            rules: [{
                                                required: true,
                                                message: 'champ requis',
                                            }],
                                            initialValue: isEditing && documentState.data ?
                                                documentState.data.year : year ? year : moment().format('YYYY'),
                                        })(
                                            <Input type="number" placeholder="année"/>,
                                        )}
                                    </Form.Item>
                                </Col>
                            </Row>
                            <Row gutter={16}>
                                <Col span={12}>
                                    <Form.Item label="Nom">
                                        {getFieldDecorator('name', {
                                            rules: [{
                                                required: true,
                                                message: 'champ requis',
                                            }],
                                            initialValue: isEditing && documentState.data ?
                                                documentState.data.name : file ? file.name : '',
                                        })(
                                            <Input placeholder="Nom de fichier"/>,
                                        )}
                                    </Form.Item>
                                </Col>
                                <Col span={12}>
                                    <Form.Item label="Description">
                                        {getFieldDecorator('description', {
                                            rules: [{
                                                required: true,
                                                message: 'champ requis',
                                            }],
                                            initialValue: isEditing && documentState.data ?
                                                documentState.data.description : '',
                                        })(
                                            <Input placeholder="Description"/>,
                                        )}
                                    </Form.Item>
                                </Col>
                            </Row>
                            <hr/>
                            {!creates.loading && creates.error && (
                                <Form.Item>
                                    <Alert
                                        message={creates.error.data.status !== 404 ?
                                            'Une erreur est survenue: ' + createErrorMessage :
                                            `Les formats supportés sont: ${Object.keys(MimeTypes).join(', ')}`
                                        }
                                        type="error"
                                    />
                                </Form.Item>
                            )}
                            {!updates.loading && updates.error && (
                                <Form.Item>
                                    <Alert
                                        message={'Une erreur est survenue: ' + updateErrorMessage}
                                        type="error"
                                    />
                                </Form.Item>
                            )}
                            <div className="form-actions">
                                <Button
                                    htmlType="submit"
                                    type="primary"
                                    onClick={onSubmit}
                                    loading={updates.loading || creates.loading}
                                >
                                    {id !== undefined ? 'Mettre à jour' : 'Ajouter'}
                                </Button>
                                <Divider type="vertical"/>
                                <Button onClick={onDrawerClose} type="ghost">
                                    Annuler
                                </Button>
                            </div>
                        </Form>
                    )}
                </Spin>
            </Drawer>
        </>
    );
};

const mapStateToProps = (state: MainReducerState) => ({
    creates: getDocumentCreateState(state),
    documentState: getDocumentDetailsState(state),
    sectionsState: getSectionsListState(state),
    updates: getDocumentUpdateState(state),
});

const DocumentFormDrawer = Form.create<DocumentDrawerProps>()(DocumentDrawer);

export default connect(
    mapStateToProps,
    {
        createDocument: create.trigger,
        getDetails: details.trigger,
        updateDocument: update.trigger,
        cleanDetails: clean.trigger,
        getStructure: structure.trigger,
    },
)(DocumentFormDrawer);
